import { GeoJsonPoint } from "../../../../../common/maphelpers";
import Route from "../../../../../common/Route";
import MapPlugin from "../MapPlugin";
import { Checkbox, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import LayerSettings from "../../common/LayerSettings";
import { useCallback, useEffect, useState } from "react";
import { useOperationsMap } from "../../OperationsMapProvider";
import { useTranslation } from "react-i18next";
import VehicleRouteFetcher from "../common/VehicleRouteFetcher";
import axios from "axios";


export default class VehiclesLayer extends MapPlugin {

  constructor(map) {
    super("vehicles", map);

    this.defaultSettings = {
      driverFilter: "",
      showRoute: false,
    };

    this._timeout = 45;
    this._routeFetcher = new VehicleRouteFetcher(this.theme);

    this.addImage("nav-arrow");
    this.addImage("car");

    this._timer = null; // TODO poll
    this._fleetIntegrations = [];
    this._nextIntegrationExpires = null;
    this._fleetData = new Map();
  }

  onMount() {
    const p = this.theme.palette;
    this.addModel("car-model", "truck/source/model.gltf");

    this._map.addSource("vehicles", {
      type: "geojson",
      cluster: true,
      clusterRadius: 25,
      data: Route.GeoJson(
        this._ops.vehicles
          .filter(t => this.isValidVehicle(t))
          .map(t => this.convertToFeature(t))
      )
    });


    this._map.addSource("vehicles-route-paths", {
      type: "geojson",
      data: Route.EmptyGeoJson()
    });

    this._map.addLayer({
      type: "line",
      source: "vehicles-route-paths",
      id: "vehicles-route-paths-layer",
      slot: "elevation-2",
      paint: {
        "line-color": ["get", "color"],
        "line-width": 5,
        "line-opacity": 0.5
      },
      layout: {
        "line-cap": "round"
      }
    });

    this._map.addLayer({
      id: "vehicles-model",
      source: "vehicles",
      slot: "elevation-2",
      type: "model",
      minzoom: 14,
      layout: {
        "model-id": "car-model",
      },
      paint: {
        "model-type": "location-indicator",
        "model-rotation": ["get", "rotation"],
        "model-scale": [7, 7, 7]
      }
    });

    this._map.addLayer({
      id: "vehicles-text",
      source: "vehicles",
      slot: "elevation-2",
      filter: ["!", ["has", "point_count"]],
      type: "symbol",
      layout: {
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-field": ["get", "title"],
        "text-font": [
          "Open Sans Semibold",
          "Arial Unicode MS Bold"
        ],
        "text-size": 10,
        "text-offset": [0, -1.25],
        "text-anchor": "bottom"
      },
      paint: {
        "text-halo-color": "#fff",
        "text-halo-width": 2
      }
    });

    this._map.addLayer({
      id: "vehicles-icon",
      source: "vehicles",
      slot: "elevation-2",
      filter: ["!", ["has", "point_count"]],
      type: "symbol",
      maxzoom: 14,
      layout: {
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-size": 2,
        "icon-image": ["get", "icon"],
        "icon-rotate": ["get", "heading"],
        "icon-rotation-alignment": "map",
      },
      paint: {
        "icon-color": ["get", "color"],
      }
    });


    this._map.addLayer({
      id: "vehicles-cluster-circles",
      source: "vehicles",
      slot: "elevation-2",
      type: "circle",
      filter: ["has", "point_count"],
      paint: {
        "circle-stroke-width": 2,
        "circle-stroke-color": "#FFF",
        "circle-color": p.divider,
        "circle-radius": 16
      }
    });

    this._map.addLayer({
      id: "vehicles-cluster-text",
      source: "vehicles",
      type: "symbol",
      slot: "elevation-2",
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 11,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-size": 1.8,
        "icon-image": "car",
        "icon-offset": [0, -3],
        "text-offset": [0, 0.7]
      },
      paint: {
        "icon-color": p.text.secondary,
        "text-halo-color": "#fff",
        "text-halo-width": 1
      }
    });

    this._timer = setInterval(() => this.onPoll(), 15000);
    this.onPoll();

    if (this.settings.showRoute) {
      this.onUpdateRouteData(this._ops.vehicles);
    } else {
      this._map.setLayoutProperty("vehicles-route-paths-layer", "visibility", "none");
    }

  }


  async onPoll() {
    await this.onUpdateFleetIntegrations(this._ops.fleetIntegrations);
    this.onUpdateVehicleData(this._ops.vehicles);
  }

  onUnMount() {
    // clear update timer
    clearInterval(this._timer);

    // remove layers
    this._map.removeLayer("vehicles-model");
    this._map.removeLayer("vehicles-text");
    this._map.removeLayer("vehicles-icon");
    this._map.removeLayer("vehicles-cluster-circles");
    this._map.removeLayer("vehicles-cluster-text");
    this._map.removeLayer("vehicles-route-paths-layer");

    // remove source
    this._map.removeSource("vehicles");
    this._map.removeSource("vehicles-route-paths");

  }

  onUpdateVehicleData(vehicles) {
    if (!this.destroyed) {


      this._map?.getSource("vehicles")?.setData(
        Route.GeoJson(
          vehicles
            .filter(t => this.isValidVehicle(t))
            .map(t => this.convertToFeature(t))
        )
      );

      this.onUpdateRouteData(vehicles);
    }
  }


  async onUpdateFleetIntegrations(fleetIntegrations) {
    if (!this.destroyed) {
      // TODO set fleet integrations
      this._fleetIntegrations = fleetIntegrations;
      if (this._fleetIntegrations.length > 0) {
        await this.fetchFleetFromIntegrations();
      }
    }
  }


  async fetchFleetFromIntegrations() { // TODO poll from time to time.
    try {
      let now = new Date();

      if (this._nextIntegrationExpires !== null && this._nextIntegrationExpires > now) {
        return;
      }

      const res = await axios(
        `/api/integration/fleet/units/list/all`,
        {
          headers: { Authorization: `Bearer ${await this.getAccessToken()}` },
        }
      );

      const fleetIds = Object.keys(res.data?.fleets);

      for (let i = 0; i < fleetIds.length; i++) {
        const fleetId = fleetIds[i];

        if (!fleetId) continue;

        const fleet = res.data.fleets[fleetId];
        const integration = this._ops.fleetIntegrations.find(t => t.id === fleetId);

        if (!integration || !fleet || !integration?.fleetMappings) continue;

        const expires = new Date(fleet.expires);

        if (this._nextIntegrationExpires === null || expires < this._nextIntegrationExpires) {
          this._nextIntegrationExpires = expires;
        }

        let svcIds = Object.keys(integration.fleetMappings);

        for (let j = 0; j < svcIds.length; j++) {
          let entityId = svcIds[j];
          let fleetUnitId = integration.fleetMappings[entityId];

          if (!!entityId && !!fleetUnitId) {
            let fleetUnit = fleet.units.find(t => t.id === fleetUnitId);

            if (fleetUnit.lng !== 0 && fleetUnit.lat !== 0) {
              this._fleetData.set(entityId, { ...fleetUnit, lastUpdate: new Date(fleetUnit.lastUpdate) })
            }
          }
        }
      }

    } catch (err) {
      console.log(err);
    }
  }


  async onUpdateRouteData(vehicles) {
    if (!this.destroyed && this.settings.showRoute) {
      await this._routeFetcher.update(vehicles.filter(t => this.isValidVehicle(t)).map(t => this.mergeVehicleWithFleet(t)));

      if (!this.destroyed) {
        this._map?.getSource("vehicles-route-paths")?.setData(
          Route.GeoJson(this._routeFetcher.getRouteGeometries())
        );
      }
    }
  }

  isValidVehicle(vehicle) {
    return (
      this._fleetData.has(vehicle.id)
    ) || (
        !!vehicle?.lastReportedLocation?.timestamp &&
        VehiclesLayer.MinutesSince(vehicle.lastReportedLocation.timestamp) < this._timeout &&
        (this.settings.driverFilter === "" || this.settings.driverFilter === vehicle.driver?.id)
      );
  }

  onUpdateSettings(settings, update) {
    this.onUpdateVehicleData(this.vehicles);

    if (update.showRoute) {
      this.onUpdateRouteData(this._ops.vehicles);
    }

    this._map.setLayoutProperty("vehicles-route-paths-layer", "visibility", !!settings.showRoute ? "visible" : "none");
  }

  convertToFeature(vehicle) {
    const mVehicle = this.mergeVehicleWithFleet(vehicle);
    const p = this.theme.palette;
    const lastLocation = mVehicle.lastReportedLocation;
    const moving = typeof lastLocation.coords.heading === "number";
    const heading = lastLocation.coords.heading || 0;
    const minutesSince = Math.floor(VehiclesLayer.MinutesSince(lastLocation.timestamp));

    return GeoJsonPoint(
      [
        lastLocation.coords.longitude,
        lastLocation.coords.latitude,
      ],
      {
        title: `${(mVehicle.title || "").replace(/[^\p{L}\p{N}\p{P}\p{Z}^$\n]/gu, '')},\n${(lastLocation?.user?.name || "").replace(/[^\p{L}\p{N}\p{P}\p{Z}^$\n]/gu, '')}${!!minutesSince ? `\n(~${minutesSince}m)` : ""}`,
        color: moving ? p.info.main : p.text.secondary,
        icon: moving ? "nav-arrow" : "car",
        heading: heading,
        rotation: [0, 0, heading + 180]
      }
    );
  }

  mergeVehicleWithFleet(vehicle) {
    if (!this._fleetData.has(vehicle.id)) return vehicle;

    let fleetUnit = this._fleetData.get(vehicle.id);
    let lastModifiedEntity = new Date(vehicle.lastModified);

    if (lastModifiedEntity > fleetUnit.lastUpdate && this.hasLocation(vehicle)) return vehicle;

    return {
      ...vehicle,
      lastReportedLocation: {
        ...vehicle.lastReportedLocation,
        timestamp: Date.now(),
        coords: {
          accuracy: 10,
          heading:  typeof fleetUnit.speed === "number" && fleetUnit.speed > 1 ? fleetUnit.direction : undefined,
          latitude: fleetUnit.lat,
          longitude: fleetUnit.lng
        }
      }
    };
  }

  hasLocation(serviceVehicle) {
    if (!serviceVehicle || !serviceVehicle.lastReportedLocation || !serviceVehicle.lastReportedLocation.coords) return false;

    const coords = serviceVehicle.lastReportedLocation.coords;
    const lat = coords.latitude;
    const lng = coords.longitude;

    return (typeof lat === "number" && lat !== 0) && (typeof lng === "number" && lat !== 0);
  }

  static MinutesSince(ts) {
    if (typeof ts !== "number") return 99999;
    return (Date.now() - ts) / 60000.0;
  }

}

export function VehiclesLayerSettings() {
  const { t } = useTranslation();
  const { operationsMap, drivers } = useOperationsMap();
  const settings = operationsMap.getSettings("vehicles");
  const [driverFilter, setDriverFilter] = useState(settings.driverFilter);
  const [showRoute, setShowRoute] = useState(settings.showRoute);
  const [active, setActive] = useState(operationsMap.isPluginActive("vehicles"));


  useEffect(() => {
    const fn = () => {
      setDriverFilter(settings.driverFilter);
      setShowRoute(settings.showRoute);
      setActive(operationsMap.isPluginActive("vehicles"));
    };

    operationsMap.on("reset", fn);
    return () => operationsMap.off("reset", fn);
  }, [operationsMap, settings]);

  // Handle plugin active state
  const onHandleAccordion = useCallback((_, val) => { // TODO handle this better, mount and unmount based on accordion open
    setActive(val);

    if (val) {
      operationsMap.activatePlugin("vehicles");
    } else {
      operationsMap.deactivatePlugin("vehicles");
    }
  }, [operationsMap]);

  const onHandleShowRoute = useCallback((ev) => {
    const val = ev.target.checked;
    setShowRoute(val);
    operationsMap.updateSettings("vehicles", { showRoute: val });
  }, [operationsMap]);

  const onHandleDriver = useCallback((ev) => {
    const val = ev.target.value;
    setDriverFilter(val);
    operationsMap.updateSettings("vehicles", { driverFilter: val });
  }, [operationsMap]);

  return (
    <LayerSettings title={t("operationsMap:layers:vehicles:title")} open={active} onChange={onHandleAccordion}>
      <Stack spacing={1}>

        <FormControl fullWidth size="small" variant="filled">
          <InputLabel>{t("operationsMap:layers:vehicles:driver")}</InputLabel>
          <Select
            onChange={onHandleDriver}
            value={driverFilter || ""}
          >
            <MenuItem value="">{t("common:all")}</MenuItem>
            {drivers.map((driver, value) => <MenuItem value={driver.id} key={value}>{driver.name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormGroup>
          <FormControlLabel control={<Checkbox checked={showRoute} onChange={onHandleShowRoute} />} label={t("operationsMap:layers:vehicles:showDestinationRoute")} />
        </FormGroup>

      </Stack>
    </LayerSettings>
  );
}



